/**
 * People-attribution rework S10 (plan §13, D-K/D-J/D-L/D-M): the «Traffic into
 * this funnel» view — a PURE render of the served `FunnelTrafficSourcesData`.
 * The popup and the page both render this component; it computes nothing:
 * shares, shared spend, CAC, ROAS, the pLTV grain and every caption come from
 * the server (AC-106). Chrome follows the S6 Traffic sources panel (same
 * table, same cells, same words) plus the share line under the source name
 * (S12 §15.2, merged with the former Share column) and the adjacent «CAC» |
 * «pLTV» columns (D-J by adjacency). S13 (plan §17): ONE cell grammar for the
 * row — the decision number in the ladder's top-line typography, evidence as
 * captions — the cost ladder under the three gates (First → second, Paywall,
 * Offer = accepted trial or purchase; served `costPer*`), a 2-line CAC and a
 * PAGE-LOCAL 3-line pLTV cell (the shared chip stays untouched elsewhere);
 * Window spend / Meta purchases / Cost per person left the column set (window
 * spend = the share block's second line, Meta = the footer sentence). The unit
 * control is a Select (F3 pattern): a unit switch REFETCHES a different
 * cohort, so it must not look like the instant toggle. S13 wave D (§17.1
 * item 11): the two tabs link to each other by campaign on the CLIENT —
 * «Creatives (N) →» under a campaign's share block filters the Creatives tab
 * to that campaign (chip, «N of M», the Campaign column hidden), the campaign
 * name in a creative row does the same, and «campaign row →» jumps back with
 * the campaign row HIGHLIGHTED (never filtered — the served total keeps its
 * context). Rows are filtered, never summed.
 */
import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Link,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { FUNNEL_REPORTS_TEST_IDS } from '../testIds';
import type { FunnelUnit } from '../funnelUnit';
import { formatInteger, formatNullableCurrency } from '../formatters';
import { attributionModelCopy } from '../attributionModelCopy';
import type { CacBeside } from './PltvWithCac';
import { formatMinorMoney } from './creativeDecisions/creativeDecisionFormat';
import {
  MetricAbsenceCell,
  RateEvidenceCell,
  SortableEvidenceTable,
  nextSortState,
  type EvidenceColumn,
  type EvidenceRow,
  type SortState,
} from './FunnelReportsDashboard';
import { PEOPLE_ABSENCE_LABEL } from '../funnelUnit';
import { buildRowZones, type ZoneReading } from '../pricingRecommendations';
import type { TrafficMoneyView, TrafficSourcesView } from '../trafficSourcesRows';
import {
  buildFunnelTrafficCampaignRows,
  buildFunnelTrafficCreativeRows,
  buildFunnelTrafficTotalRow,
  type FunnelTrafficRow,
  type TrafficLadderGateView,
  type TrafficPltvView,
  type TrafficShareView,
} from '../funnelTrafficSourcesRows';
import type { FunnelTrafficSourcesData } from '../types';

export interface FunnelTrafficSourcesViewProps {
  state: 'idle' | 'loading' | 'ready' | 'failed';
  data?: FunnelTrafficSourcesData;
  error?: string;
  unit: FunnelUnit;
  /** A unit change is a REFETCH (different cohort) — the parent owns the request. */
  onUnitChange: (unit: FunnelUnit) => void;
  view: TrafficSourcesView;
  onViewChange: (view: TrafficSourcesView) => void;
  /** S13 wave D: the selected Meta campaign — a FILTER on Creatives, a HIGHLIGHT on Campaigns; the parent owns it (URL / dialog state). */
  selectedCampaignId?: string;
  /** S13 wave D: one call sets the tab AND the campaign (undefined clears it). */
  onCampaignSelect?: (selection: { view: TrafficSourcesView; campaignId?: string }) => void;
  scopeChips: string[];
  currency?: string;
  /** S12 §15.3: creative-grain watch-only chips deep-link into Creative Decisions of this project. */
  projectId?: string;
  /** S12 §15.3: the Creative-Decisions UI flag (same value as Reports) — without it the watch-only band never renders. */
  watchOnlyPltvEnabled?: boolean;
  /**
   * Phase 2-min (AC-038/045): the page-built «Unit economics →» href for a
   * CAMPAIGN row — undefined = no action (creative rows, «(not set)», rows
   * with nothing seedable). Only the PAGE passes it; the popup stays as-is.
   */
  rowEconomicsHref?: (row: FunnelTrafficRow) => string | undefined;
  /**
   * Phase 2 chips (AC-069/072): the zoned-bound window the served figures
   * were fetched for — with it the view computes the per-campaign-row zone
   * readings ON OPEN (memoized per payload; sort/unit changes reuse it).
   * Absent → no verdict lines (the popup form stays chip-free).
   */
  zonesWindow?: { from: string; to: string };
}

// S13 §17.1 items 3/9: the cost ladder — one number per gate, served.
const GATE_COST_NOTE = 'The third line is what one person who reached this step cost: the row\'s shared spend ÷ the people who reached it (served, never derived on the page). The ladder reads left to right — second screen, paywall, accepted offer — and ends at CAC, the price of a NEW PAYING person.';
const OFFER_NOTE = 'People who ACCEPTED the offer on the paywall — started a trial OR bought outright (the funnel\'s conversion). Paid people are CAC\'s denominator in the next columns; an accepted trial is not yet a payment.';
// S11 §14.2: CAC and pLTV are TWO metrics with DIFFERENT denominators — each
// header says whose money it is. They stay adjacent (D-J) so pLTV never reads
// without the number it must beat.
const CAC_NOTE = 'Cost to acquire ONE new paying person: shared spend ÷ people whose FIRST paid conversion happened on a journey credited to this row (last click, the served model\'s window). Never spend ÷ trials. Compare with pLTV in the next column. Break-even (third line) = CAC ÷ the first-cycle price of the row\'s leading product (most subscriptions): how many paid checks one acquired subscriber must cover; «so far» = that cohort\'s average paid checks per subscriber to date.';
// S12 §15.3: the cell holds exactly ONE money figure by precedence — the
// decision projection, else the campaign's watch-only band (I4 grammar, n =
// its subscriptions PROJECT-wide), else the served withholding reason.
const PLTV_NOTE = 'Projected 12-month lifetime value of ONE subscriber acquired on this row (gross-collected basis, P10–P90 band). Its denominator is subscribers, not new paying people — so it is not the same metric as CAC; the two sit side by side because pLTV must beat CAC. The grain says whose projection it is: this version, or campaign-wide when the version has none. When no decision projection is served, the source\'s WATCH-ONLY band rides the cell instead — gross per subscriber across every funnel it feeds (n = its subscriptions project-wide), never a verdict input.';
const ROAS_NOTE = 'Realized gross-collected LTV of this row\'s subscriptions ÷ its shared spend — a payback ratio, not a projection. The cell caption names the basis: collected checks to date over covered spend. Subscription checkouts carry no Purchase-event amounts, so the event-revenue ROAS is a typed absence on subscription funnels (never 0.00).';
// §6 #8 (owner, 2026-09-02): the projected twin of ROAS as its own column.
const PLTV_ROAS_NOTE = 'Projected 12-month pLTV : CAC for this row (served pltv12Roas with its P10–P90 band). Color reads the interval BOUNDS against break-even 1×: red = under 1× even at P90, green = clears 1× from P10 up, amber = the band straddles 1×. The 1× bound and the coloring are OUR policy, not a vendor rule; n and the prior-informed qualifier stay visible. Too-young cohorts and older payloads are typed absences — never 0×.';
// S12 §15.2: the share lives INSIDE the source cell (name / id / share line
// + bar) — it is an attribute of the row, not a metric sorted apart from it.
const SHARE_NOTE = 'The share line under the name: the share of this source\'s traffic across the WHOLE project (started people, or sessions in visits mode) that reached THIS version; the line below it names the source\'s full window spend the share apportions. A campaign feeds several funnels and placements; the denominator is all of them.';
// S12 §15.1b: the shared spend's basis, stated once in the header guide.
// S13: the former «Window spend» column's guide lives here too.
const WINDOW_SPEND_NOTE = 'The source\'s FULL spend in the window, across every funnel and placement it feeds — the number the share apportions — is the last line of the source cell («of $X window spend»). Shared spend ÷ window spend = the share line under the name.';
const SPEND_NOTE = `The spend this scope earned. At ad × day grain when served: exact on ad-days that fed only this scope, apportioned by people on shared ad-days («N ad-days exact · M shared» under the number; parts in the tooltip); ad-days without a linked person are shared by the campaign's project-wide people share. Otherwise window spend × the share line. ${WINDOW_SPEND_NOTE}`;
// S12 §15.4a: subscriptions by billing class, as the pLTV cohort serves them.
// S13 §17.1 item 7: one row per product (Stripe price) — the label is
// computed (interval · first-paid-cycle price · trial entry), ids in the tooltip.
const PRODUCTS_NOTE = 'The row\'s subscriptions by PRODUCT, from the subscription fact of its linked journeys — one line per Stripe price (product × interval × price), most subscribed first. The price is the first PAID cycle (a paid-trial fee is never the price; «from $X» when subscribers paid different first cycles); the trial part names the entry — «7-day free trial», «3-day paid trial $0.99». Under each product: its subscriptions and, when it has trials, how many converted («trial→paid 60 / 92»). Ids in the tooltip; the same words at campaign and creative grain. Older payloads fall back to billing classes. Checks = billed paid cycles on the GROSS basis; a trial fee is never a check; an annual product at 1 check per subscription in a short window is expected. Realized gross per subscriber, refunds and cohort age ride the tooltip.';

const pltvGrainLabel = (grain: TrafficPltvView['grain']): string | undefined => (
  grain === 'campaign' ? 'campaign-wide' : grain === 'version' ? 'this version' : undefined
);

// S13: ONE cell grammar for the row — the decision number in the ladder's
// top-line typography (= RateEvidenceCell's top line), evidence as captions.
const TOP_LINE_SX = { fontSize: '0.75rem', fontWeight: 700, lineHeight: 1.15 } as const;
const CAPTION_SX = { fontWeight: 500, lineHeight: 1.15 } as const;

function TopLine({ children, disabled = false, title }: { children: React.ReactNode; disabled?: boolean; title?: string }): React.ReactElement {
  return (
    <Typography variant="body2" color={disabled ? 'text.disabled' : 'text.primary'} title={title} sx={{ ...TOP_LINE_SX, fontWeight: disabled ? 500 : 700 }}>
      {children}
    </Typography>
  );
}

function CaptionLine({ children, disabled = false, title, nowrap = false }: { children: React.ReactNode; disabled?: boolean; title?: string; nowrap?: boolean }): React.ReactElement {
  return (
    <Typography variant="caption" color={disabled ? 'text.disabled' : 'text.secondary'} title={title} sx={{ ...CAPTION_SX, ...(nowrap ? { whiteSpace: 'nowrap' } : {}) }}>
      {children}
    </Typography>
  );
}

/**
 * S13 §17.1 item 6: the page-local 3-line pLTV band grammar for the served
 * watch-only snapshot — exact cents (the row's own exponent), raw n. The
 * shared `WatchOnlyPltvChip` is NOT rendered here and NOT modified.
 */
function watchOnlyLines(watchOnly: NonNullable<TrafficPltvView['watchOnly']>): { value: string; band: string; n: string } | undefined {
  const available = watchOnly.state === 'available' && watchOnly.p50Minor !== undefined && Boolean(watchOnly.originalCurrency);
  if (!available) return undefined;
  const money = (minor: number): string => formatMinorMoney(minor, watchOnly.originalCurrency!, watchOnly.currencyExponent ?? 2, { narrow: true });
  return {
    value: `≈ ${money(watchOnly.p50Minor!)}`,
    band: `${money(watchOnly.p10Minor ?? watchOnly.p50Minor!)}–${money(watchOnly.p90Minor ?? watchOnly.p50Minor!)}`,
    n: `n = ${formatInteger(watchOnly.ownSubscriptions)} · watch-only`,
  };
}

export function FunnelTrafficSourcesView({
  state,
  data,
  error,
  unit,
  onUnitChange,
  view,
  onViewChange,
  selectedCampaignId,
  onCampaignSelect,
  scopeChips,
  currency,
  projectId,
  watchOnlyPltvEnabled = false,
  rowEconomicsHref,
  zonesWindow,
}: FunnelTrafficSourcesViewProps): React.ReactElement {
  const peopleMode = unit === 'people';
  const [sortState, setSortState] = React.useState<SortState>({ columnId: 'spend', direction: 'desc' });
  const campaignRows = React.useMemo(() => (data ? buildFunnelTrafficCampaignRows(data, unit) : []), [data, unit]);
  // Phase 2 chips (AC-072): computed once per served payload («on open») —
  // sorting and filtering reuse the memo; a unit change refetches data and
  // recomputes naturally. Pure client math over SERVED row figures.
  const rowVerdicts = React.useMemo(
    () => (zonesWindow && data ? buildRowZones(campaignRows, zonesWindow) : undefined),
    [campaignRows, data, zonesWindow],
  );
  const creativeRows = React.useMemo(() => (data ? buildFunnelTrafficCreativeRows(data, unit) : []), [data, unit]);
  const totalRow = React.useMemo(() => (data ? buildFunnelTrafficTotalRow(data, unit) : undefined), [data, unit]);
  const versionScoped = data?.scope.versionScoped ?? true;
  // S13 wave D: the campaign ↔ creative linkage, an equality on the served
  // ids. Counting rows is not arithmetic on people — nothing is summed.
  const creativeCountByCampaign = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of creativeRows) if (row.campaignId) counts.set(row.campaignId, (counts.get(row.campaignId) ?? 0) + 1);
    return counts;
  }, [creativeRows]);
  const campaignRowById = React.useMemo(() => new Map(campaignRows.filter(row => row.campaignId).map(row => [row.campaignId!, row])), [campaignRows]);
  const selectedCampaignRow = selectedCampaignId ? campaignRowById.get(selectedCampaignId) : undefined;
  const selectedCampaignName = selectedCampaignRow?.label
    ?? creativeRows.find(row => row.campaignId === selectedCampaignId)?.campaignLabel
    ?? selectedCampaignId;
  const visibleCreativeRows = React.useMemo(
    () => (selectedCampaignId ? creativeRows.filter(row => row.campaignId === selectedCampaignId) : creativeRows),
    [creativeRows, selectedCampaignId],
  );
  const canSelect = Boolean(onCampaignSelect);
  const selectCampaign = (nextView: TrafficSourcesView, campaignId?: string): void => onCampaignSelect?.({ view: nextView, campaignId });
  // The reverse jump lands on the highlighted row — scroll it into view once per selection.
  React.useEffect(() => {
    if (view !== 'campaigns' || !selectedCampaignRow || state !== 'ready') return;
    const node = document.querySelector<HTMLElement>(`[data-testid="${FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelRow('campaign', selectedCampaignRow.id)}"]`);
    const tr = node?.closest('tr');
    // jsdom has no scrollIntoView — guard, never assume the browser API.
    if (tr && typeof tr.scrollIntoView === 'function') tr.scrollIntoView({ block: 'center' });
  }, [view, selectedCampaignRow, state]);
  const modelId = data?.scope.attribution?.selectedModelId;
  const modelLabel = modelId ? attributionModelCopy(modelId).label : undefined;

  const costNoun = peopleMode ? 'person' : 'journey';
  // S13 §17.1 items 2/3/9: no bar; the served gate cost is the third line
  // («$11.68 / person»), a typed dash with its reason when the lane served
  // null, nothing at all on a pre-S13 payload or an unavailable gate.
  const gateCostLine = (cost: TrafficMoneyView | undefined): React.ReactNode => {
    if (!cost) return null;
    // REVIEW S13: a money figure never wraps («$125.83 /» + «person» read as two facts).
    if (typeof cost.value === 'number') return <Typography variant="caption" color="text.secondary" sx={{ ...CAPTION_SX, whiteSpace: 'nowrap' }}>{`${formatNullableCurrency(cost.value, currency)} / ${costNoun}`}</Typography>;
    return <CaptionLine disabled title={cost.reason}>{`— · ${cost.reason ?? 'no cost served'}`}</CaptionLine>;
  };
  const gateCell = (key: string, gate: TrafficLadderGateView): React.ReactNode => (
    gate.available
      ? (
        <Stack key={key} spacing={0} sx={{ minWidth: 0 }}>
          <RateEvidenceCell rate={gate.rate ?? null} numerator={gate.numerator ?? 0} denominator={gate.denominator ?? 0} />
          {gateCostLine(gate.cost)}
        </Stack>
      )
      : <MetricAbsenceCell key={key} detail={gate.reason ?? PEOPLE_ABSENCE_LABEL} />
  );
  const moneyCell = (money: TrafficMoneyView): React.ReactNode => {
    if (money.value === undefined) return <MetricAbsenceCell detail={money.reason ?? PEOPLE_ABSENCE_LABEL} />;
    if (money.value === null) {
      return (
        <Typography variant="body2" color="text.disabled" title={money.reason}>
          {money.reason ? `— · ${money.reason}` : '—'}
        </Typography>
      );
    }
    return <Typography variant="body2">{formatNullableCurrency(money.value, currency)}</Typography>;
  };
  // S12 §15.1b: the shared spend names its basis — «N ad-days exact · M
  // shared» when the lane served ad × day money; the parts ride the tooltip.
  const spendCell = (row: FunnelTrafficRow): React.ReactNode => {
    const evidence = row.spend;
    if (typeof evidence.value !== 'number' || evidence.basis !== 'ad_day') return moneyCell(row.spend);
    const parts = [
      `exact ${formatNullableCurrency(evidence.exact ?? 0, currency)} on ${formatInteger(evidence.adDaysExact ?? 0)} ad-days that fed only this scope`,
      `apportioned ${formatNullableCurrency(evidence.apportioned ?? 0, currency)} on ${formatInteger(evidence.adDaysShared ?? 0)} shared ad-days`,
      ...((evidence.adDaysUnlinked ?? 0) > 0 ? [`${formatNullableCurrency(evidence.campaignShared ?? 0, currency)} by campaign share on ${formatInteger(evidence.adDaysUnlinked ?? 0)} ad-days without a linked person`] : []),
    ].join(' · ');
    return (
      // REVIEW Must-fix: one nowrap caption clipped on the left in the 130 px
      // column — two short lines instead («357 ad-days exact» / «61 shared»).
      <Stack spacing={0} alignItems="flex-end" title={parts}>
        <Typography variant="body2">{formatNullableCurrency(evidence.value, currency)}</Typography>
        <Typography variant="caption" color="text.secondary">{`${formatInteger(evidence.adDaysExact ?? 0)} ad-days exact`}</Typography>
        <Typography variant="caption" color="text.secondary">{`${formatInteger(evidence.adDaysShared ?? 0)} shared`}</Typography>
      </Stack>
    );
  };
  // S12 §15.2 / S13 item 1: the share line lives under the source name (no
  // bar); the former «Window spend» column is its second line, mirrored into
  // the block's title. The testid stays for the readers.
  const shareLine = (row: FunnelTrafficRow): React.ReactNode => {
    const share: TrafficShareView = row.share;
    if (!share.available) {
      return share.reason && !row.isNoCampaign
        ? (
          <Typography variant="caption" color="text.disabled" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelShareCell(row.kind, row.id)}>
            {`share n/a · ${share.reason}`}
          </Typography>
        )
        : null;
    }
    const windowSpendLine = typeof row.spend.windowSpend === 'number' ? `of ${formatNullableCurrency(row.spend.windowSpend, currency)} window spend` : undefined;
    return (
      <Box data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelShareCell(row.kind, row.id)} sx={{ maxWidth: 220 }} title={windowSpendLine}>
        <RateEvidenceCell rate={share.rate ?? null} numerator={share.numerator ?? 0} denominator={share.denominator ?? 0} />
        {windowSpendLine && <CaptionLine>{windowSpendLine}</CaptionLine>}
      </Box>
    );
  };
  // S11 §14.2: CAC and pLTV are two adjacent cells (the D-J pair by adjacency,
  // guarded in pltvWithCac.guard.test.ts). S13 §17.1 item 8: two lines — the
  // price in the ladder's top-line typography, the people as a dimmed caption
  // (the typed absence keeps its count on line 2, S11 §14.3 #3). Page-local:
  // `cacLineText` stays the one wording on every other surface.
  // S13 C1: `breakEven` = the served «BE 6.3 · so far 1.9 / sub» third line
  // (rows only — the Total row never passes one, people are non-additive).
  const cacLines = (cac: CacBeside, testId?: string, breakEven?: string): React.ReactNode => {
    const count = typeof cac.count === 'number' && (cac.value !== null || cac.count > 0)
      ? `${formatInteger(cac.count)} new paying people`
      : undefined;
    const absent = cac.value === undefined || cac.value === null;
    return (
      <Stack spacing={0} sx={{ minWidth: 0 }} data-testid={testId}>
        {absent
          ? <TopLine disabled title={cac.reason}>{`n/a · ${cac.reason ?? (cac.value === undefined ? 'people data needs refresh' : 'no paid conversions yet')}`}</TopLine>
          : <TopLine title={cac.reason}>{formatNullableCurrency(cac.value, currency)}</TopLine>}
        {count && <CaptionLine>{count}</CaptionLine>}
        {breakEven && <CaptionLine nowrap title="Break-even = CAC ÷ the leading product's first-cycle price; «so far» = its cohort's paid checks per subscriber to date.">{breakEven}</CaptionLine>}
      </Stack>
    );
  };
  const cacCell = (row: FunnelTrafficRow): React.ReactNode => cacLines(row.cac, FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCacCell(row.kind, row.id), row.cacBreakEven);
  // S13 §17.1 item 6: a PAGE-LOCAL 3-line pLTV cell — value / band / grain
  // or «n = N · watch-only» — in the ladder's typography. Precedence is
  // unchanged (S12 §15.3): decision projection → watch-only band (flag on)
  // → typed absence. The creative-grain band keeps its Creative-Decisions
  // deep link; the shared chip is neither rendered nor modified here.
  const pltvCell = (row: FunnelTrafficRow): React.ReactNode => {
    const grain = pltvGrainLabel(row.pltv.grain);
    const watchOnly = watchOnlyPltvEnabled && row.pltv.watchOnly ? row.pltv.watchOnly : undefined;
    const band = watchOnly ? watchOnlyLines(watchOnly) : undefined;
    const linkable = Boolean(watchOnly?.deploymentKey && projectId);
    return (
      <Stack spacing={0} sx={{ minWidth: 0 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelPltvCell(row.kind, row.id)}>
        {typeof row.pltv.value === 'number' ? (
          <>
            <TopLine>{`≈ ${formatNullableCurrency(row.pltv.value, currency)}`}</TopLine>
            {typeof row.pltv.p10 === 'number' && typeof row.pltv.p90 === 'number' && (
              <CaptionLine>{`${formatNullableCurrency(row.pltv.p10, currency)}–${formatNullableCurrency(row.pltv.p90, currency)}`}</CaptionLine>
            )}
            {grain && <CaptionLine>{grain}</CaptionLine>}
          </>
        ) : watchOnly ? (
          band ? (
            <>
              <Typography
                variant="body2"
                color={linkable ? 'primary.main' : 'text.primary'}
                sx={linkable ? { ...TOP_LINE_SX, textDecoration: 'underline' } : TOP_LINE_SX}
                data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelWatchOnlyChip(row.kind, row.id)}
                title={linkable
                  ? 'Watch-only gross 12-month pLTV forecast per subscriber. Costs are not included; this never drives kill/scale. Opens the exact deployment band history.'
                  : 'Watch-only gross 12-month pLTV forecast per subscriber for this campaign. Costs are not included; this never drives kill/scale.'}
                {...(linkable ? { component: 'a', href: `/project/${projectId}/reports/creative-decisions?deployment=${encodeURIComponent(watchOnly.deploymentKey!)}` } : {})}
              >
                {band.value}
              </Typography>
              <CaptionLine>{band.band}</CaptionLine>
              {/* REVIEW S13: «watch-only» is load-bearing — never split at its hyphen. */}
              <Typography variant="caption" color="text.secondary" sx={{ ...CAPTION_SX, whiteSpace: 'nowrap' }}>{band.n}</Typography>
            </>
          ) : (
            <>
              <TopLine disabled>n/a</TopLine>
              <CaptionLine disabled>{`not decision-grade yet (n = ${formatInteger(watchOnly.ownSubscriptions)})`}</CaptionLine>
            </>
          )
        ) : (
          // Typed absence: «n/a» + the served reason as a caption; the
          // self-heal note rides the tooltip.
          <>
            <TopLine disabled title={[row.pltv.reason, row.pltv.note].filter(Boolean).join(' · ')}>n/a</TopLine>
            <CaptionLine disabled>{row.pltv.reason ?? 'no projection served'}</CaptionLine>
          </>
        )}
      </Stack>
    );
  };
  // S11 §14.3 #1: the number is the realized-LTV ROAS (header states the
  // basis once); a row that only has event revenue says so; a structural 0 is
  // a typed absence with its reason, never «0.00».
  const roasCell = (row: FunnelTrafficRow): React.ReactNode => (
    typeof row.roas.value === 'number'
      ? (
        <Stack spacing={0} alignItems="flex-end">
          <Typography variant="body2">{`${row.roas.value.toFixed(2)}×`}</Typography>
          {/* AC-070 (§6 #8a): the cell NAMES its basis — never a bare ratio. */}
          {row.roas.basis === 'realized_ltv' && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>collected checks to date</Typography>
          )}
          {row.roas.basis === 'event_revenue' && <Typography variant="caption" color="text.secondary">event revenue</Typography>}
        </Stack>
      )
      : (
        // Review round 4 (B-3): the FULL reason turned into a 7-line tower in
        // the fixed 92px column — the visible label keeps only the reason's
        // first clause; the complete sentence stays in the tooltip.
        <Typography variant="body2" color="text.disabled" title={row.roas.reason} sx={{ textAlign: 'right' }}>
          {row.roas.reason ? `— · ${row.roas.reason.split(' · ')[0]}` : '—'}
        </Typography>
      )
  );
  // §6 #8b (owner, 2026-09-02): the pLTV ROAS cell — served pltv12Roas toned
  // by its interval BOUNDS against break-even 1× (the only client threshold
  // read); typed absences, never 0×; n stays visible (AC-071).
  const pltvRoasCell = (row: FunnelTrafficRow): React.ReactNode => (
    <Stack
      spacing={0}
      alignItems="flex-end"
      data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelPltvRoasCell(row.kind, row.id)}
      title="Projected 12-month pLTV : CAC. Color reads the served P10–P90 band against break-even 1× — under 1× even at P90 (red), clears 1× from P10 up (green), band straddling 1× (amber). Bounds and coloring are our policy, not a vendor rule."
    >
      {typeof row.pltvRoas.value === 'number' ? (
        <>
          <Typography
            variant="body2"
            // Round-0 precedent (dark error→error.light 5.09:1): semantic MAIN
            // tones fail 4.5:1 on the dark paper (#f44336 = 4.14, #2e7d32 =
            // 2.97) — dark mode reads the LIGHT variant of the tone.
            sx={{
              fontWeight: 500,
              color: theme => (row.pltvRoas.tone
                ? (theme.palette.mode === 'dark' ? theme.palette[row.pltvRoas.tone].light : theme.palette[row.pltvRoas.tone].main)
                : undefined),
            }}
          >
            {`${row.pltvRoas.value.toFixed(2)}×`}
          </Typography>
          {typeof row.pltvRoas.p10 === 'number' && typeof row.pltvRoas.p90 === 'number' && (
            <CaptionLine>{`band ${row.pltvRoas.p10.toFixed(2)}–${row.pltvRoas.p90.toFixed(2)}×`}</CaptionLine>
          )}
          {row.pltvRoas.reading && (
            <CaptionLine>
              {[row.pltvRoas.reading, typeof row.pltvRoas.n === 'number' ? `n = ${formatInteger(row.pltvRoas.n)}` : undefined, row.pltvRoas.lowConfidence ? 'prior-informed' : undefined].filter(Boolean).join(' · ')}
            </CaptionLine>
          )}
        </>
      ) : (
        <>
          <TopLine disabled>n/a</TopLine>
          <CaptionLine disabled>{row.pltvRoas.reason ?? 'no projection served'}</CaptionLine>
        </>
      )}
    </Stack>
  );
  // S12 §15.4a: «258 monthly · 137 upfront» + «of 395 subscriptions»; only
  // served classes; a typed absence names why.
  // S13 §17.1 item 7: one row per product (price_id) when the lane served
  // the list — label / «N subscriptions · trial→paid k / n»; the billing
  // classes stay the fallback for a pre-S13 payload (AC-S13-037).
  const productsCell = (row: FunnelTrafficRow): React.ReactNode => (
    <Stack spacing={0} sx={{ minWidth: 0 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelProductsCell(row.kind, row.id)}>
      {row.products.items && row.products.items.length > 0 ? (
        row.products.items.map(item => (
          <Stack key={item.key} spacing={0} sx={{ minWidth: 0, '& + &': { mt: 0.5 } }} title={item.title} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelProductRow(row.kind, row.id, item.key)}>
            <TopLine disabled={item.unknown}>{item.label}</TopLine>
            <CaptionLine>{[`${formatInteger(item.subscriptions)} subscriptions`, item.trialLine].filter(Boolean).join(' · ')}</CaptionLine>
            {/* S13 C1: the served paid-checks line (absent without paid cycles). */}
            {item.checksLine && <CaptionLine>{item.checksLine}</CaptionLine>}
          </Stack>
        ))
      ) : row.products.classes.length > 0 ? (
        <>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {row.products.classes.map(entry => `${formatInteger(entry.count)} ${entry.label}`).join(' · ')}
          </Typography>
          {typeof row.products.subscriptions === 'number' && (
            <Typography variant="caption" color="text.secondary">{`of ${formatInteger(row.products.subscriptions)} subscriptions`}</Typography>
          )}
          {row.products.trials && (
            // S12 §15.4b: the trial entry line — only served, non-zero parts.
            <Typography variant="caption" color="text.secondary">
              {`${formatInteger(row.products.trials.started)} trials: ${[
                ...(row.products.trials.weekly > 0 ? [`${formatInteger(row.products.trials.weekly)} weekly`] : []),
                ...(row.products.trials.monthly > 0 ? [`${formatInteger(row.products.trials.monthly)} monthly`] : []),
                ...(row.products.trials.annual > 0 ? [`${formatInteger(row.products.trials.annual)} annual`] : []),
                ...(row.products.trials.paid > 0 ? [`${formatInteger(row.products.trials.paid)} paid`] : []),
                `${formatInteger(row.products.trials.converted)} converted`,
              ].join(' · ')}`}
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" color="text.disabled" title={row.products.reason}>
          {row.products.reason ? `— · ${row.products.reason}` : '—'}
        </Typography>
      )}
    </Stack>
  );
  // Phase 2 chips (AC-069): the zone reading under the source name — a state
  // chip plus its caption. Threshold readings only, never command verbs; a
  // row with no reading (missing lanes) renders NO chip.
  // Review round 4 (A-P0-1/B-1): the Campaign column is FIXED-width, so a
  // reason inside the chip ellipsizes at every viewport — the chip carries
  // the fixed ZONE word (proven non-truncating) and the FULL reading wraps
  // in the caption below. Round-0 precedent (B-2): dark mode renders the
  // LIGHT variant of the semantic tone (error.main = 4.14:1 on dark paper).
  const ZONE_TONE: Record<ZoneReading['zone'], 'error' | 'warning' | 'info'> = { act: 'error', test: 'warning', wait: 'info' };
  const ZONE_WORD: Record<ZoneReading['zone'], string> = { act: 'act without a test', test: 'experiment zone', wait: 'wait for a date' };
  const zoneWord = (reading: ZoneReading): string =>
    reading.zone === 'wait' && !reading.date ? 'wait for data' : ZONE_WORD[reading.zone];
  const verdictLine = (row: FunnelTrafficRow): React.ReactNode => {
    const reading = rowVerdicts?.get(row.id);
    if (!reading) return null;
    const tone = ZONE_TONE[reading.zone];
    return (
      <Stack spacing={0} sx={{ mt: 0.5 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelRowVerdict(row.id)}>
        <Chip
          size="small"
          variant="outlined"
          label={zoneWord(reading)}
          sx={theme => ({
            alignSelf: 'flex-start',
            color: theme.palette.mode === 'dark' ? theme.palette[tone].light : theme.palette[tone].main,
            borderColor: theme.palette.mode === 'dark' ? theme.palette[tone].light : theme.palette[tone].main,
          })}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>{reading.reason}</Typography>
      </Stack>
    );
  };
  const sourceNameCell = (row: FunnelTrafficRow): React.ReactNode => (
    <Stack spacing={0} sx={{ minWidth: 0 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelRow(row.kind, row.id)}>
      <Typography variant="body2" sx={{ fontWeight: row.isNoCampaign ? 400 : 500, overflowWrap: 'anywhere' }}>{row.label}</Typography>
      {row.sublabel && <Typography variant="caption" color="text.secondary">{row.sublabel}</Typography>}
      {row.identityPlane === 'behavioural' && !row.isNoCampaign && (
        <Typography variant="caption" color="text.disabled">no ad account</Typography>
      )}
      {versionScoped && shareLine(row)}
      {row.kind === 'campaign' && verdictLine(row)}
      {row.kind === 'campaign' && canSelect && campaignCreativesLink(row)}
      {row.kind === 'campaign' && rowEconomicsLink(row)}
    </Stack>
  );
  // Phase 2-min (AC-038): «Unit economics →» opens the hub calculator in a
  // NEW TAB, prefilled from this row's SERVED figures (the seeds ride the
  // URL). The href builder decides eligibility — creative rows and rows with
  // nothing seedable render no action (AC-045).
  const rowEconomicsLink = (row: FunnelTrafficRow): React.ReactNode => {
    const href = rowEconomicsHref?.(row);
    if (!href) return null;
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener"
        variant="caption"
        underline="hover"
        title="Open the unit-economics calculator prefilled from this row's served figures (new tab)"
        data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelRowEconomics(row.id)}
        sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {/* Review P1-3: ↗ = leaves this page (new tab); → stays the in-page drill glyph. */}
        Unit economics ↗
      </Link>
    );
  };
  // S13 wave D (AC-S13D-007/008): «Creatives (N) →» under the share block —
  // N = this campaign's rows on the Creatives tab; the absences say why.
  const campaignCreativesLink = (row: FunnelTrafficRow): React.ReactNode => {
    const count = row.campaignId ? creativeCountByCampaign.get(row.campaignId) ?? 0 : 0;
    if (!row.campaignId || count === 0) {
      const reason = !row.campaignId
        ? `no creatives · ${row.isNoCampaign ? 'no campaign' : 'no ad account'}`
        : 'no creatives served';
      return <Typography variant="caption" color="text.disabled" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignCreativesAbsence(row.id)}>{reason}</Typography>;
    }
    return (
      <Link
        component="button"
        type="button"
        variant="caption"
        underline="hover"
        onClick={() => selectCampaign('creatives', row.campaignId)}
        title={`Open the Creatives tab filtered to this campaign — ${formatInteger(count)} creatives`}
        data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignCreativesLink(row.campaignId)}
        sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 500, whiteSpace: 'nowrap' }}
      >
        {`Creatives (${formatInteger(count)}) →`}
      </Link>
    );
  };
  // S13 wave D (AC-S13D-012/013/017): the Campaign cell on the Creatives tab —
  // the name FILTERS this tab, «campaign row →» jumps to the highlighted
  // campaign row; a creative whose campaign has no row says so.
  const creativeCampaignCell = (row: FunnelTrafficRow): React.ReactNode => {
    if (!row.campaignId || !canSelect) return <Typography variant="body2" color="text.secondary">{row.campaignLabel ?? '—'}</Typography>;
    const hasCampaignRow = campaignRowById.has(row.campaignId);
    return (
      <Stack spacing={0} sx={{ minWidth: 0 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => selectCampaign('creatives', row.campaignId)}
          title="Show only this campaign's creatives"
          data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCreativeCampaignFilter(row.id)}
          sx={{ textAlign: 'left', overflowWrap: 'anywhere' }}
        >
          {row.campaignLabel ?? row.campaignId}
        </Link>
        {hasCampaignRow ? (
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => selectCampaign('campaigns', row.campaignId)}
            title="Open the Campaigns tab with this campaign's row highlighted"
            data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCreativeCampaignJump(row.id)}
            sx={{ alignSelf: 'flex-start', mt: 0.5, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            campaign row →
          </Link>
        ) : (
          <Typography variant="caption" color="text.disabled" title="This campaign has no row on the Campaigns tab — none of its people started this version in the window." data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCreativeCampaignAbsence(row.id)}>
            no campaign row · no traffic into this version
          </Typography>
        )}
      </Stack>
    );
  };

  const shareLabel = peopleMode ? 'started people' : 'sessions';
  const sourceUnitNote = versionScoped ? `share of its project-wide ${shareLabel} → this version` : undefined;
  // S13 §17.1 as AMENDED by §6 #8 (owner, 2026-09-02): 11 columns on the
  // Campaigns tab / 12 on Creatives — the pLTV ROAS column is the ONE
  // sanctioned addition; Window spend, Meta purchases and Cost per person
  // stay out (share line, footer sentence, hidden).
  const gateUnitNote = `cost per ${costNoun} who reached it`;
  const baseColumns: EvidenceColumn[] = [
    { id: 'spend', label: 'Spend', sortable: true, align: 'right', minWidth: 112, unitNote: versionScoped ? 'shared' : undefined, description: versionScoped ? SPEND_NOTE : undefined },
    // Round 4 (A-P1-6/B-11): 62px let the right-aligned header abut the next
    // column's label — widened so the two headers separate at every width.
    { id: 'people', label: peopleMode ? 'People' : 'Journeys', sortable: true, align: 'right', minWidth: 74 },
    // REVIEW S13: the arrow glyph keeps the label on one line at 102 px (the ASCII «->» wrapped).
    { id: 'firstToSecond', label: 'First\u00a0→\u00a0second', sortable: true, minWidth: 108, unitNote: gateUnitNote, description: GATE_COST_NOTE },
    { id: 'paywall', label: 'Paywall', sortable: true, minWidth: 102, unitNote: gateUnitNote, description: GATE_COST_NOTE },
    // S13 item 9: the accepted offer (trial OR purchase), not the paid set.
    { id: 'offer', label: 'Offer', sortable: true, minWidth: 102, unitNote: `${peopleMode ? `cost per ${costNoun} who accepted it · ` : ''}trial or purchase`, description: `${OFFER_NOTE} ${GATE_COST_NOTE}` },
    // D-J by ADJACENCY: `cac` is immediately followed by `pltv` (guarded).
    // S13 C1: 140 px so «BE 6.3 · so far 1.9 / sub» never wraps to three lines (mock REVIEW note).
    { id: 'cac', label: 'CAC', sortable: true, minWidth: 140, unitNote: 'per new paying person', description: CAC_NOTE },
    { id: 'pltv', label: 'pLTV', sortable: true, minWidth: 120, unitNote: 'per subscriber · 12-mo', description: PLTV_NOTE },
    { id: 'roas', label: 'ROAS', sortable: true, align: 'right', minWidth: 92, unitNote: 'collected to date', description: ROAS_NOTE },
    // §6 #8 (owner, 2026-09-02): the ONE sanctioned addition to the S13
    // column set — projected payback beside the realized one.
    { id: 'pltvRoas', label: 'pLTV ROAS', sortable: true, align: 'right', minWidth: 116, unitNote: 'projected · vs 1×', description: PLTV_ROAS_NOTE },
    { id: 'products', label: 'Products', sortable: true, minWidth: 196, unitNote: 'subscriptions by product', description: PRODUCTS_NOTE },
  ];
  const campaignColumns: EvidenceColumn[] = [
    { id: 'source', label: 'Campaign', sortable: true, minWidth: 176, unitNote: sourceUnitNote, description: versionScoped ? SHARE_NOTE : undefined },
    ...baseColumns,
  ];
  const creativeColumns: EvidenceColumn[] = [
    { id: 'source', label: 'Creative', sortable: true, minWidth: 152, unitNote: sourceUnitNote, description: versionScoped ? SHARE_NOTE : undefined },
    { id: 'campaign', label: 'Campaign', sortable: true, minWidth: 102 },
    ...baseColumns,
  ];
  const sortValuesFor = (row: FunnelTrafficRow): EvidenceRow['sortValues'] => ({
    source: row.label,
    campaign: row.campaignLabel ?? '',
    spend: row.spend.value ?? null,
    people: row.people.value ?? null,
    firstToSecond: row.firstToSecond.available ? row.firstToSecond.rate ?? null : null,
    paywall: row.paywall.available ? row.paywall.rate ?? null : null,
    offer: row.offer.available ? row.offer.rate ?? null : null,
    cac: row.cac.value ?? null,
    pltv: row.pltv.value ?? null,
    roas: row.roas.value ?? null,
    pltvRoas: row.pltvRoas.value ?? null,
    products: row.products.subscriptions ?? (row.products.classes.length ? row.products.classes.reduce((sum, entry) => sum + entry.count, 0) : null),
  });
  const rowCells = (row: FunnelTrafficRow, withCampaign: boolean): React.ReactNode[] => [
    sourceNameCell(row),
    ...(withCampaign ? [<React.Fragment key="campaign">{creativeCampaignCell(row)}</React.Fragment>] : []),
    spendCell(row),
    row.people.value === undefined
      ? <MetricAbsenceCell key="people" detail={row.people.reason ?? PEOPLE_ABSENCE_LABEL} />
      : formatInteger(row.people.value),
    gateCell('first-second', row.firstToSecond),
    gateCell('paywall', row.paywall),
    gateCell('offer', row.offer),
    cacCell(row),
    pltvCell(row),
    roasCell(row),
    pltvRoasCell(row),
    productsCell(row),
  ];
  const totalEvidenceRow: EvidenceRow | null = totalRow ? {
    key: 'total',
    group: '__total',
    cells: [
      <Stack key="total" spacing={0} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelTotalRow}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{totalRow.label}</Typography>
        <Typography variant="caption" color="text.secondary">computed on the scope, not summed from rows</Typography>
      </Stack>,
      <Stack key="total-spend" spacing={0} alignItems="flex-end">
        {totalRow.spend.value === null || totalRow.spend.value === undefined
          ? <Typography variant="body2" color="text.disabled">—</Typography>
          : <Typography variant="body2">{formatNullableCurrency(totalRow.spend.value, currency)}</Typography>}
        <Typography variant="caption" color="text.secondary">Σ shared spend</Typography>
      </Stack>,
      formatInteger(totalRow.people),
      gateCell('total-first-second', totalRow.firstToSecond),
      gateCell('total-paywall', totalRow.paywall),
      gateCell('total-offer', totalRow.offer),
      <React.Fragment key="total-cac">{cacLines(totalRow.cac)}</React.Fragment>,
      <MetricAbsenceCell key="total-pltv" detail="scope projection is not served" />,
      <Typography key="total-roas" variant="body2" color="text.disabled">—</Typography>,
      <MetricAbsenceCell key="total-pltv-roas" detail="scope projection is not served" />,
      <Typography key="total-products" variant="body2" color="text.disabled" title="Products are per source; people on several campaigns would double-count in a scope sum.">—</Typography>,
    ],
    sortValues: {},
  } : null;
  // S13 wave D: the Campaigns tab is never filtered — the selected row is
  // highlighted; the Creatives tab is filtered and drops its Campaign column
  // (the chip carries the name) while ONE campaign is selected.
  const creativesFiltered = Boolean(selectedCampaignId);
  const campaignEvidenceRows: EvidenceRow[] = campaignRows.map(row => ({
    key: `campaign-${row.id}`,
    cells: rowCells(row, false),
    sortValues: sortValuesFor(row),
    ...(selectedCampaignId && row.campaignId === selectedCampaignId ? { selected: true } : {}),
  }));
  const creativeEvidenceRows: EvidenceRow[] = visibleCreativeRows.map(row => ({ key: `creative-${row.id}`, cells: rowCells(row, !creativesFiltered), sortValues: sortValuesFor(row) }));
  const visibleCreativeColumns = creativesFiltered ? creativeColumns.filter(column => column.id !== 'campaign') : creativeColumns;
  const selectedCount = selectedCampaignId ? creativeCountByCampaign.get(selectedCampaignId) ?? 0 : 0;
  // The strip above the caption names the selection and clears it (chip ×).
  const campaignStrip = selectedCampaignId && (view === 'creatives' || selectedCampaignRow) ? (
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignStrip}>
      <Typography variant="caption" color="text.secondary">{view === 'creatives' ? 'Filter:' : 'Highlighted:'}</Typography>
      <Chip
        size="small"
        color="primary"
        variant="outlined"
        label={`Campaign: ${selectedCampaignName}`}
        onDelete={() => selectCampaign(view, undefined)}
        deleteIcon={<span aria-label={view === 'creatives' ? 'Show all creatives' : 'Clear the highlight'} role="img">×</span>}
        data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignFilterChip}
      />
      {view === 'creatives' ? (
        <Typography variant="caption" color="text.secondary">
          {`${formatInteger(selectedCount)} of ${formatInteger(creativeRows.length)} creatives`}
          {selectedCampaignRow && (
            <>
              {' · '}
              <Link component="button" type="button" variant="caption" underline="hover" onClick={() => selectCampaign('campaigns', selectedCampaignId)} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignStripLink}>campaign row →</Link>
            </>
          )}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary">
          <Link component="button" type="button" variant="caption" underline="hover" onClick={() => selectCampaign('creatives', selectedCampaignId)} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCampaignStripLink}>
            {`its ${formatInteger(selectedCount)} creatives →`}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;

  const scope = data?.scope.people;
  const adScope = data?.scope.creativesPeople;
  const campaignsCaption = data
    ? (peopleMode
      ? `People. ${scope ? `${formatInteger(scope.startedPeople)} people started this version; ${formatInteger(scope.peopleOnMultipleCampaigns)} came through more than one campaign — rows do not add up, the total row is served de-duplicated.` : 'Scope total unavailable on this payload.'} ${data.captions.spendRule} Offer is credited to the campaign of the session it happened in (last click${modelLabel ? `, ${modelLabel}` : ''}), as Meta does.`
      : `Visits. Journeys credited to each campaign (last click). ${data.captions.spendRule} Paywall has no journey twin on this lane — switch to People for the full ladder. CAC stays per person.`)
    : '';
  const creativesCaption = data
    ? (peopleMode
      ? `People. One row per creative (ad); identity from the paid-acquisition linkage. ${adScope ? `${formatInteger(adScope.startedPeople)} people attributable to a creative (${formatInteger(adScope.journeysAttributable)} of ${formatInteger(adScope.journeysInScope)} journeys) · ${formatInteger(adScope.peopleOnMultipleAds)} came through more than one creative — rows do not add up.` : 'Scope total unavailable on this payload.'} ${data.captions.spendRule.replace("campaign's", "ad's")}`
      : `Visits. Sessions the ad resolved on our side; the people ladder is on the People unit. ${data.captions.spendRule.replace("campaign's", "ad's")}`)
    : '';

  return (
    <Stack spacing={1.5} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelView}>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary">Scope:</Typography>
        {scopeChips.map(chip => <Chip key={chip} size="small" variant="outlined" label={chip} />)}
        {modelLabel && <Chip size="small" variant="outlined" label={`Attribution: ${modelLabel}`} />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">Unit</Typography>
        <Select
          size="small"
          value={unit}
          disabled={state === 'loading'}
          onChange={event => {
            const next = event.target.value === 'visits' ? 'visits' : 'people';
            if (next !== unit) onUnitChange(next);
          }}
          data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelUnitSelect}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="people">People</MenuItem>
          <MenuItem value="visits">Visits</MenuItem>
        </Select>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Switching the unit recomputes the shares on the server (a different cohort) — it is not the instant page toggle.
      </Typography>
      {state === 'loading' && (
        <Stack alignItems="center" spacing={1} sx={{ py: 6 }} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelLoading}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Computing this version&apos;s share of each campaign… the acquisition lane can take a minute.
          </Typography>
        </Stack>
      )}
      {state === 'failed' && <Alert severity="warning">{error ?? 'Traffic sources could not be loaded.'}</Alert>}
      {state === 'ready' && data && (
        <>
          <Tabs value={view} onChange={(_event, next: TrafficSourcesView) => onViewChange(next)} aria-label="Traffic into this funnel">
            <Tab value="campaigns" label={`Campaigns (${campaignRows.length})`} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelTab('campaigns')} />
            <Tab value="creatives" label={creativesFiltered ? `Creatives (${formatInteger(selectedCount)} of ${formatInteger(creativeRows.length)})` : `Creatives (${creativeRows.length})`} data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelTab('creatives')} />
          </Tabs>
          {campaignStrip}
          {view === 'campaigns' ? (
            <>
              <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCaption}>{campaignsCaption}</Typography>
              {campaignEvidenceRows.length === 0 ? (
                <Alert severity="info">No campaign brought traffic into this version in the window — widen the window or check the placement.</Alert>
              ) : (
                <SortableEvidenceTable
                  id="traffic-into-funnel-campaigns"
                  columns={campaignColumns}
                  rows={[...(totalEvidenceRow ? [totalEvidenceRow] : []), ...campaignEvidenceRows]}
                  sortState={sortState}
                  onSort={columnId => setSortState(current => nextSortState(current, columnId))}
                  maxHeight="60vh"
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {`Total row is served on the scope, not summed from rows. ${data.captions.pltv} ${data.captions.meta}`}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" data-testid={FUNNEL_REPORTS_TEST_IDS.trafficIntoFunnelCaption}>{creativesCaption}</Typography>
              {creativeEvidenceRows.length === 0 ? (
                <Alert severity="info">
                  {creativesFiltered
                    ? `No creative of this campaign is linked to journeys of this version in the window — clear the filter to see all ${formatInteger(creativeRows.length)}.`
                    : 'No creative is linked to journeys of this version in the window.'}
                </Alert>
              ) : (
                <SortableEvidenceTable
                  id="traffic-into-funnel-creatives"
                  columns={visibleCreativeColumns}
                  rows={creativeEvidenceRows}
                  sortState={sortState}
                  onSort={columnId => setSortState(current => nextSortState(current, columnId))}
                  maxHeight="60vh"
                />
              )}
              <Typography variant="caption" color="text.secondary">
                {/* S13 item 1: the Meta sentence (no number) at ad grain — the column is gone. */}
                {`Showing ${creativesFiltered ? `${formatInteger(selectedCount)} of ${formatInteger(creativeRows.length)} creatives · filtered to ${selectedCampaignName}` : `${formatInteger(creativeRows.length)} creatives`} · sorted by shared spend${data.creativesDataThrough ? ` · delivery facts through ${data.creativesDataThrough}` : ''}. ${data.captions.meta.replace(/campaign's/g, "ad's").replace(/campaign/g, 'ad')}`}
              </Typography>
            </>
          )}
        </>
      )}
    </Stack>
  );
}
